import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Archive, Mail, Trash2 } from 'lucide-react'
import type { SiteEnquiry, WebsiteApi } from '@/api/website'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

const FILTERS = [['', 'All'], ['New', 'New'], ['Read', 'Read'], ['Archived', 'Archived'], ['Spam', 'Spam']] as const

/** The messages visitors have sent through the contact form on the website. */
export function EnquiriesCard({ api }: { api: WebsiteApi }) {
  const qc = useQueryClient()
  const [status, setStatus] = useState('')
  const [open, setOpen] = useState<string | null>(null)
  const key = ['site-enquiries', api.base, status]
  const { data, isLoading } = useQuery({ queryKey: key, queryFn: () => api.getEnquiries(status || undefined) })

  const refresh = () => { qc.invalidateQueries({ queryKey: ['site-enquiries', api.base] }); qc.invalidateQueries({ queryKey: ['website-status'] }) }
  const setState = useMutation({ mutationFn: (v: { id: string; status: string }) => api.setEnquiryStatus(v.id, v.status), onSuccess: refresh })
  const remove = useMutation({ mutationFn: (id: string) => api.deleteEnquiry(id), onSuccess: refresh })

  const toggle = (e: SiteEnquiry) => {
    setOpen(open === e.id ? null : e.id)
    if (e.status === 'New') setState.mutate({ id: e.id, status: 'Read' })
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <CardTitle className="text-base">
          Messages from your website
          {data && data.newCount > 0 && <span className="ml-2 text-xs font-medium bg-amber-100 text-amber-800 rounded-full px-2 py-0.5">{data.newCount} new</span>}
        </CardTitle>
        <div className="flex gap-1 flex-wrap">
          {FILTERS.map(([value, label]) => (
            <button key={value} type="button" onClick={() => setStatus(value)}
              className={`text-xs px-2.5 py-1 rounded-full border cursor-pointer ${status === value ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}>{label}</button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading && <p className="text-sm text-gray-400">Loading…</p>}
        {data && data.items.length === 0 && <p className="text-sm text-gray-400">No messages yet. They'll appear here when someone uses the contact form on your website.</p>}
        <ul className="divide-y divide-gray-100">
          {data?.items.map(e => (
            <li key={e.id} className="py-3">
              <button type="button" onClick={() => toggle(e)} className="w-full text-left flex items-start gap-3 cursor-pointer">
                <span className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${e.status === 'New' ? 'bg-amber-500' : 'bg-transparent'}`} />
                <span className="flex-1 min-w-0">
                  <span className="flex items-center gap-2 text-sm text-gray-900">
                    <span className={e.status === 'New' ? 'font-semibold' : ''}>{e.name}</span>
                    <span className="text-gray-400 truncate">{e.email}</span>
                  </span>
                  <span className="block text-sm text-gray-500 truncate">{e.message}</span>
                </span>
                <span className="text-xs text-gray-400 whitespace-nowrap">{new Date(e.receivedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
              </button>
              {open === e.id && (
                <div className="mt-3 ml-5 space-y-3">
                  <p className="text-sm text-gray-800 whitespace-pre-wrap bg-gray-50 border border-gray-100 rounded-lg px-3 py-2.5">{e.message}</p>
                  <p className="text-xs text-gray-500">
                    {e.phone && <>Phone: <a href={`tel:${e.phone.replace(/[^\d+]/g, '')}`} className="text-indigo-600">{e.phone}</a> · </>}
                    {e.pageSlug ? `Sent from /${e.pageSlug}` : 'Sent from the home page'}
                    {e.deliveryStatus === 'Failed' && ' · The email notification failed, so check here for new messages'}
                  </p>
                  <div className="flex flex-wrap gap-3 text-sm">
                    <a href={`mailto:${e.email}?subject=${encodeURIComponent('Re: your message')}`} className="inline-flex items-center gap-1.5 text-indigo-600 hover:underline"><Mail size={14} /> Reply</a>
                    <button type="button" className="inline-flex items-center gap-1.5 text-gray-600 hover:text-gray-900 cursor-pointer" onClick={() => setState.mutate({ id: e.id, status: e.status === 'Archived' ? 'Read' : 'Archived' })}>
                      <Archive size={14} /> {e.status === 'Archived' ? 'Unarchive' : 'Archive'}
                    </button>
                    {e.status !== 'Spam' && <button type="button" className="text-gray-600 hover:text-gray-900 cursor-pointer" onClick={() => setState.mutate({ id: e.id, status: 'Spam' })}>Mark as spam</button>}
                    <button type="button" className="inline-flex items-center gap-1.5 text-red-600 hover:text-red-700 cursor-pointer" onClick={() => { if (confirm('Delete this message?')) remove.mutate(e.id) }}>
                      <Trash2 size={14} /> Delete
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  )
}
